import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./About.css";

const API =
  process.env.REACT_APP_API_URL ||
  "https://backend-9i6n.onrender.com";

/* Convert English digits to Marathi (Devanagari) numerals.
   Numbers get Indian-style grouping (२०,०००); strings keep their
   non-digit chars (e.g. "362.2" → "३६२.२", "60 कि.मी." → "६० कि.मी."). */
const DEV = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
const toMarathiNum = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  const s = typeof val === "number" ? val.toLocaleString("en-IN") : String(val);
  return s.replace(/\d/g, (d) => DEV[d]);
};

/* Same decorative village photo already used on the Home hero — kept
   consistent since the village model has no dedicated photo field. */
const VILLAGE_IMAGE = "https://images.unsplash.com/photo-1586773860418-d37222d8fce3";

/* ---------- small inline icon set (page-local) ---------- */
const Icon = {
  people: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
    </svg>
  ),
  male: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#1466A8" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="7" r="4" /><path d="M10 11a6 6 0 0 0-6 6v3h12v-3a6 6 0 0 0-6-6z" />
    </svg>
  ),
  female: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#E8871E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="7" r="4" /><path d="M12 11v10M9 18h6" />
    </svg>
  ),
  area: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  heritage: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#E8871E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8v13M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" /><rect x="3" y="8" width="18" height="4" rx="1" />
    </svg>
  ),
  admin: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
    </svg>
  ),
  clean: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" /><path d="M2 21c0-3 1.85-5.36 5.08-6" />
    </svg>
  ),
  edu: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  ),
  growth: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
    </svg>
  ),
  water: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1466A8" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.5S6 9 6 14a6 6 0 0 0 12 0c0-5-6-11.5-6-11.5z" />
    </svg>
  ),
  trash: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  ),
  book: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E8871E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  ),
  road: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21V9l6-4v16M3 21h18M5 21V11l4-2M15 5l4 2v14" />
    </svg>
  ),
  pin: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1466A8" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  ),
  help: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01" />
    </svg>
  ),
  chevron: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0F5C2E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  contact: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
};

const VISION = [
  { title: "पारदर्शक व डिजिटल प्रशासन", desc: "ऑनलाइन सेवा व माहितीद्वारे नागरिकाभिमुख कारभार.", icon: Icon.admin },
  { title: "स्वच्छ व हरित गाव वातावरण", desc: "स्वच्छता, वृक्षारोपण व घनकचरा व्यवस्थापनावर भर.", icon: Icon.clean },
  { title: "दर्जेदार शिक्षण व आरोग्य", desc: "शाळा, अंगणवाडी व प्राथमिक आरोग्य सुविधा.", icon: Icon.edu },
  { title: "सशक्त विकास व रोजगार", desc: "पायाभूत सुविधा व रोजगार हमीतून ग्राम विकास.", icon: Icon.growth },
];

const INFRA = [
  {
    title: "पाणीपुरवठा", icon: Icon.water, bg: "#EAF2FB",
    rows: [
      ["सार्वजनिक विहिरी", "2"],
      ["बोअरवेल / हातपंप", "2"],
      ["पाण्याच्या टाक्या", "1"],
    ],
  },
  {
    title: "स्वच्छता", icon: Icon.trash, bg: "#E8F3EA",
    rows: [
      ["सार्वजनिक शौचालये", "1"],
      ["कचराकुंड्या", "30"],
      ["घनकचरा प्रकल्प", "2"],
    ],
  },
  {
    title: "शिक्षण", icon: Icon.book, bg: "#FBEFE3",
    rows: [
      ["प्राथमिक शाळा", "2"],
      ["माध्यमिक शाळा", "1"],
      ["उच्च माध्यमिक", "1"],
      ["अंगणवाडी", "4"],
    ],
  },
  {
    title: "पायाभूत सुविधा", icon: Icon.road, bg: "#E8F3EA",
    rows: [
      ["रस्त्यावरील पथदिवे", "172"],
      ["सार्वजनिक इमारती", "2"],
    ],
  },
  {
    title: "दळणवळण अंतर", icon: Icon.pin, bg: "#EAF2FB",
    rows: [
      ["जिल्हा मुख्यालय", "60 कि.मी."],
      ["तालुका मुख्यालय", "18 कि.मी."],
    ],
  },
];

const FAQ = [
  { q: "गावात बस सेवा उपलब्ध आहे का?", a: "होय, दररोज ठराविक वेळेत ४ वेळा बस सेवा उपलब्ध आहे.", yes: true },
  { q: "प्राथमिक आरोग्य केंद्र आहे का?", a: "होय, गावात प्राथमिक आरोग्य केंद्र कार्यरत आहे.", yes: true },
  { q: "सार्वजनिक वाचनालय उपलब्ध आहे का?", a: "होय, नागरिकांसाठी सार्वजनिक वाचनालय उपलब्ध आहे.", yes: true },
  { q: "खेळाचे मैदान आहे का?", a: "होय, गावात सुसज्ज क्रीडांगण उपलब्ध आहे.", yes: true },
  { q: "पर्यटकांसाठी होम-स्टे उपलब्ध आहे का?", a: "होय, निवडक होम-स्टे सुविधा उपलब्ध आहेत.", yes: true },
  { q: "हॉटेल व निवास व्यवस्था आहे का?", a: "होय, मर्यादित निवास व भोजन व्यवस्था उपलब्ध आहे.", yes: true },
  { q: "गावात बँकेची शाखा आहे का?", a: "सध्या गावात बँक शाखा नाही; नजीकच्या शहरात उपलब्ध.", yes: false },
];

const About = () => {
  const [village, setVillage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const res = await fetch(`${API}/api/village`);
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        setVillage(data);
      } catch (err) {
        console.error("गावाची माहिती मिळवण्यात अडचण आली", err);
        setError("गावाची माहिती लोड करता आली नाही");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const STATS = [
    { icon: Icon.people, bg: "#E8F3EA", value: village?.populationTotal, label: "एकूण लोकसंख्या" },
    { icon: Icon.male, bg: "#EAF2FB", value: village?.populationMale, label: "पुरुष" },
    { icon: Icon.female, bg: "#FBEFE3", value: village?.populationFemale, label: "महिला" },
    { icon: Icon.area, bg: "#E8F3EA", value: village?.area, label: "क्षेत्रफळ (चौ.कि.मी.)" },
  ];

  return (
    <div>
      {/* ================= HERO ================= */}
      <section className="rk-hero about-hero">
        <div className="rk-hero-inner">
          <div className="rk-crumb">मुख्यपृष्ठ / गावाची माहिती</div>
          <h1 className="rk-hero-title">
            {village?.name || "रोषणखेडा"} येथे आपले स्वागत आहे
          </h1>
          <p className="rk-hero-sub">
            स्वच्छ गाव &nbsp;•&nbsp; सशक्त समाज &nbsp;•&nbsp; उज्वल भविष्य — अमरावती जिल्ह्यातील एक प्रगतशील व निसर्गरम्य गाव.
          </p>
        </div>
      </section>

      {loading ? (
        <p className="rk-loading">गावाची माहिती लोड होत आहे...</p>
      ) : error ? (
        <p className="rk-error">⚠️ {error}</p>
      ) : (
        <>
          {/* ================= STATS RIBBON (overlapping) ================= */}
          <div className="about-ribbon-wrap">
            <div className="about-ribbon">
              {STATS.map((s, i) => (
                <div className="about-ribbon-item" key={i}>
                  <span className="about-ribbon-icon" style={{ background: s.bg }}>
                    {s.icon}
                  </span>
                  <div>
                    <div className="about-ribbon-value">{toMarathiNum(s.value)}</div>
                    <div className="about-ribbon-label">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ================= ABOUT + HISTORY ================= */}
          <section className="rk-wrap about-intro-section">
            <div className="about-intro-grid">
              <div>
                <span className="rk-eyebrow">परिचय</span>
                <h2 className="rk-h2">आमच्या गावाबद्दल</h2>
                <p className="about-intro-text">
                  {village?.description ||
                    "अमरावती जिल्ह्यातील वरूड तालुक्यात वसलेले हे गाव निसर्गरम्य व प्रगतशील आहे. सुपीक जमीन, मेहनती शेतकरी आणि एकोप्याने राहणारा समाज ही या गावाची खरी ओळख."}
                </p>

                {village?.history && (
                  <div className="about-heritage-box">
                    <div className="about-heritage-title">
                      {Icon.heritage} गावाचा वारसा
                    </div>
                    <p>{village.history}</p>
                  </div>
                )}
              </div>

              <div>
                <div className="about-snapshot-card">
                  <div className="about-snapshot-img">
                    <img src={VILLAGE_IMAGE} alt="गावाचे दृश्य" loading="lazy" />
                  </div>
                  <div className="about-snapshot-body">
                    <div className="about-snapshot-title">एका दृष्टिक्षेपात</div>
                    <div className="about-snapshot-rows">
                      <div className="about-snapshot-row">
                        <span>तालुका</span><b>वरूड</b>
                      </div>
                      <div className="about-snapshot-row">
                        <span>जिल्हा</span><b>अमरावती</b>
                      </div>
                      <div className="about-snapshot-row">
                        <span>राज्य</span><b>महाराष्ट्र</b>
                      </div>
                      <div className="about-snapshot-row">
                        <span>एकूण कुटुंबे</span><b>{toMarathiNum(village?.totalFamilies)}</b>
                      </div>
                      <div className="about-snapshot-row">
                        <span>प्रभाग (वॉर्ड)</span><b>{toMarathiNum(village?.wards)}</b>
                      </div>
                      <div className="about-snapshot-row">
                        <span>साक्षरता दर</span>
                        <b>
                          {village?.literacyRate !== null &&
                          village?.literacyRate !== undefined &&
                          village?.literacyRate !== ""
                            ? toMarathiNum(village.literacyRate) + "%"
                            : "—"}
                        </b>
                      </div>
                      <div className="about-snapshot-row about-snapshot-row--last">
                        <span>पिन कोड</span><b>{toMarathiNum("444906")}</b>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ================= VISION ================= */}
          <section className="rk-wrap about-vision-section">
            <div className="about-section-head">
              <span className="rk-eyebrow">ध्येय व दिशा</span>
              <h2 className="rk-h2">आमचे ध्येय</h2>
            </div>
            <div className="rk-cols rk-cols-4">
              {VISION.map((v, i) => (
                <div className="rk-card about-vision-card" key={i}>
                  <span className="about-vision-icon">{v.icon}</span>
                  <h3>{v.title}</h3>
                  <p>{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ================= INFRASTRUCTURE ================= */}
          <section className="rk-wrap about-infra-section">
            <span className="rk-eyebrow">पायाभूत सुविधा</span>
            <h2 className="rk-h2" style={{ marginBottom: 22 }}>ग्रामपंचायत प्राथमिक माहिती</h2>
            <div className="rk-cols rk-cols-3">
              {INFRA.map((cat, i) => (
                <div className="rk-card about-infra-card" key={i}>
                  <div className="about-infra-head">
                    <span className="about-infra-icon" style={{ background: cat.bg }}>
                      {cat.icon}
                    </span>
                    <span>{cat.title}</span>
                  </div>
                  {cat.rows.map(([label, value], j) => (
                    <div className="about-infra-row" key={j}>
                      <span>{label}</span>
                      <b>{toMarathiNum(value)}</b>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </section>

          {/* ================= FAQ ================= */}
          <section className="rk-wrap about-faq-section">
            <div className="about-section-head">
              <span className="rk-eyebrow">नागरिकांसाठी</span>
              <h2 className="rk-h2">वारंवार विचारले जाणारे प्रश्न</h2>
            </div>
            <div className="about-faq-list">
              {FAQ.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div className={`rk-card about-faq-item ${isOpen ? "open" : ""}`} key={i}>
                    <div
                      className="about-faq-q-row"
                      onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    >
                      <span className="about-faq-icon">{Icon.help}</span>
                      <span className="about-faq-q">{f.q}</span>
                      <span className="about-faq-chev">{Icon.chevron}</span>
                    </div>
                    {isOpen && (
                      <div className="about-faq-a-row">
                        <span>{f.a}</span>
                        <span className={`about-faq-badge ${f.yes ? "yes" : "no"}`}>
                          {f.yes ? "उपलब्ध" : "उपलब्ध नाही"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ================= CTA ================= */}
          <section className="rk-wrap about-cta-section">
            <div className="about-cta-card">
              <div>
                <h3>अधिक माहिती हवी आहे?</h3>
                <p>ग्रामपंचायत कार्यालयाशी संपर्क साधा किंवा शासकीय सेवांसाठी ऑनलाइन अर्ज करा.</p>
              </div>
              <div className="about-cta-actions">
                <Link to="/contact" className="about-cta-btn about-cta-btn--saffron">
                  {Icon.contact} संपर्क साधा
                </Link>
                <Link to="/services" className="about-cta-btn about-cta-btn--white">
                  सेवांसाठी अर्ज करा
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default About;
